export type RuntimeCockpitStatus =
  | 'planned'
  | 'running'
  | 'codex_claimed_done'
  | 'longwalls_verified_done'
  | 'partial'
  | 'blocked'
  | 'failed';

export type RuntimeCockpitSourceMode =
  | 'file_backed_dev'
  | 'local_cloud_simulated'
  | 'signed_cloud_ingest'
  | 'cloud_snapshot_read';

export type RuntimeCockpitCloudBridgeStatus =
  | 'connected'
  | 'stale'
  | 'disconnected'
  | 'blocked_external_dependency'
  | 'invalid_signature'
  | 'blocked_by_policy'
  | 'validation_failed';

export type RuntimeCockpitTestBadgeStatus =
  | 'planned'
  | 'running'
  | 'approved'
  | 'codex_claimed_done'
  | 'failed'
  | 'blocked';

export interface RuntimeCockpitVisualEvidence {
  id: string;
  testId: string | null;
  laneId: string | null;
  caseId: string | null;
  stage: string;
  status: string;
  capturedAt: string | null;
  route: string | null;
  viewport:
    | string
    | {
        width?: number | string | null;
        height?: number | string | null;
      }
    | null;
  hash: string | null;
  evidenceRef: string | null;
  thumbnailDataUrl?: string | null;
}

export interface RuntimeCockpitCodexExecutionProfile {
  schema?: string;
  provider?: string | null;
  model: string | null;
  modelDisplayName?: string | null;
  reasoningEffort: string | null;
  speedMode: string | null;
  fastMode: boolean;
  fastModeAllowed?: boolean;
  source?: string | null;
  claimBoundary?: string | null;
}

export interface RuntimeCockpitRuntimeTest {
  id: string;
  title: string;
  description: string;
  family: string;
  status: RuntimeCockpitTestBadgeStatus;
  stage: string;
  startedAt: string | null;
  targetEndAt: string | null;
  progressPercent: number;
  laneCount: number;
  liveLaneCount: number;
  budget: {
    status: string;
    inputTokens: number | null;
    outputTokens: number | null;
    totalTokens: number | null;
    egressMb: number;
    egressBudgetMb: number | null;
    inputTokenBudget?: number | null;
    outputTokenBudget?: number | null;
  };
  codexExecutionProfile?: RuntimeCockpitCodexExecutionProfile | null;
  gates: Array<{
    id: string;
    label: string;
    status: RuntimeCockpitTestBadgeStatus;
    details?: Record<string, unknown> | null;
    evidenceRefs: string[];
  }>;
  lanes: Array<{
    id: string;
    label: string;
    status: RuntimeCockpitTestBadgeStatus;
    runStatus?: string | null;
    heartbeatCount: number;
    checkpointCount: number;
    processAlive: boolean | null;
    caseId?: string | null;
    stopReason?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    elapsedMs?: number | null;
    elapsedMinutes?: number | null;
    elapsedHours?: number | null;
    platformElapsedHours?: number | null;
    activeCodexElapsedHours?: number | null;
    targetRuntimeHours?: number | null;
    runtimeWindowStatus?: string | null;
    longRunTelemetry?: {
      wallClockElapsedHours?: number | null;
      activeCodexTimeHours?: number | null;
      supervisorOnlyTimeHours?: number | null;
      codexOccupancyRatio?: number | null;
      usefulSegmentCount?: number | null;
      wrapperOnlySegmentCount?: number | null;
      budgetObserved?: number | null;
      budgetDelta?: number | null;
      budgetRemaining?: number | null;
      mandatoryBacklogCoverage?: number | null;
      acceptanceGapRemaining?: number | null;
      currentStopClassifier?: string | null;
      lastContinuationWorkOrderHash?: string | null;
      lastUsefulDeltaReasons?: string[];
    } | null;
    failedGateIds?: string[];
    blockers?: string[];
    inputTokens?: number | null;
    outputTokens?: number | null;
    totalTokens?: number | null;
    visualEvidence?: RuntimeCockpitVisualEvidence[];
    codexExecutionProfile?: RuntimeCockpitCodexExecutionProfile | null;
    evidenceRefs: string[];
  }>;
  visualEvidence?: RuntimeCockpitVisualEvidence[];
  evidenceRefs: string[];
  claimBoundary: string;
}

export interface RuntimeCockpitTelemetryPayload {
  ok: true;
  projectId: string;
  tenantId: string;
  mode: RuntimeCockpitSourceMode;
  cloudBridge?: {
    schema: string;
    status: RuntimeCockpitCloudBridgeStatus;
    sourceMode: RuntimeCockpitSourceMode;
    tenantId: string;
    projectId: string;
    runId: string | null;
    lastSyncAt: string | null;
    receivedAt: string | null;
    staleAfterSeconds: number;
    ageSeconds: number | null;
    sequence: number | null;
    snapshotId: string | null;
    signatureVerified: boolean;
    replaySafe: boolean;
    redacted: boolean;
    byteSize: number;
    egressEstimateMb: number;
    blockers: string[];
    evidenceRefs: string[];
  };
  aggregate: {
    generatedAt: string;
    runId: string;
    status: RuntimeCockpitStatus;
    summary: {
      eventCount: number;
      laneCount: number;
      liveLaneCount: number;
      claimedDoneCount: number;
      verifiedDoneCount: number;
      blockedLayerCount: number;
      unsafeEgressAllowed: boolean;
      apiDisabled: boolean;
      liveProviderMutationAllowed: boolean;
      codexApiCallsAllowed: boolean;
      test2Locked: boolean;
      test2LockReason: string;
    };
    lanes: Array<{
      laneId: string;
      status: RuntimeCockpitStatus;
      lastObservedAt: string | null;
      heartbeatCount: number;
      checkpointCount: number;
      processAlive: boolean | null;
      blockers: string[];
    }>;
    layers: Array<{
      layer: string;
      status: RuntimeCockpitStatus;
      claimedDone: boolean;
      verifiedDone: boolean;
      blockers: string[];
      evidenceRefs: string[];
      codexExecutionProfile?: RuntimeCockpitCodexExecutionProfile | null;
    }>;
    budget: {
      inputTokens: number | null;
      outputTokens: number | null;
      totalTokens: number | null;
      estimatedCostUsd: number | null;
      egressMb: number;
      egressBudgetMb: number | null;
      status: string;
      hardStopReasons: string[];
    };
    evidenceRefs?: string[];
    blockers: string[];
  };
  program: {
    items: Array<{
      id: string;
      title: string;
      status: RuntimeCockpitStatus;
      plannedStartAt: string | null;
      plannedEndAt: string | null;
      targetHours: number;
      targetConcurrency: number;
      canLaunch: boolean;
      lockReason: string;
      evidenceRefs: string[];
    }>;
  };
  report: {
    claimBoundary: string;
    lots: Array<{ id: string; status: string; evidenceRefs: string[]; blockers: string[] }>;
    runtimeTests?: RuntimeCockpitRuntimeTest[];
    evaluation?: { verdict?: string; score?: number; blockers?: string[] };
  };
  verifiedDone: {
    status: string;
    blockers: string[];
    layers: Array<{
      layer: string;
      status: 'accepted' | 'blocked' | 'not_claimed';
      claimedByCodex: boolean;
      verifiedByLongwalls: boolean;
      blockers: string[];
      evidenceRefs: string[];
    }>;
    claimBoundary: string;
  };
  actionLocks: {
    locks: Array<{
      action: string;
      allowed: boolean;
      requiredRole: string;
      reason: string;
      evidenceRefs: string[];
    }>;
  };
  replayProof: { accepted: boolean; hash: string; blockers: string[] };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? `Request failed with ${response.status}`);
  }
  return payload;
}

export async function fetchRuntimeCockpitTelemetry(
  projectId = 'longwalls-platform',
): Promise<RuntimeCockpitTelemetryPayload> {
  const params = new URLSearchParams({ source: 'auto', ts: String(Date.now()) });
  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/runtime-cockpit/telemetry?${params}`,
    {
      cache: 'no-store',
      headers: {
        'cache-control': 'no-store',
        pragma: 'no-cache',
      },
    },
  );
  return parseJsonResponse<RuntimeCockpitTelemetryPayload>(response);
}
